import type { TransactionSubmitted } from '@revoke.cash/core/types';
import { TransactionRevertedError } from '@revoke.cash/core/utils/errors';
import { throwIfTransactionReverted } from '@revoke.cash/core/wallet';
import type {
  Call,
  Capabilities,
  Hash,
  SendTransactionParameters,
  TransactionReceipt,
  WalletCallReceipt,
  WalletClient,
  WriteContractParameters,
} from 'viem';
import type { OnUpdate, TokenAllowanceData } from './allowances';

export type Eip5792Call = Call;

export const walletSupportsEip5792 = async (walletClient: WalletClient, chainId: number) => {
  try {
    const capabilities = (await walletClient.getCapabilities()) as Capabilities;
    console.log('Wallet supports EIP5792:', capabilities);

    if (supportsAtomicBatch(capabilities[chainId])) return true;

    console.log(`Wallet does not support EIP5792 on chain ${chainId}`);
    return false;
  } catch {
    console.log('Wallet does not support EIP5792');
    return false;
  }
};

export const supportsAtomicBatch = (chainCapabilities?: Capabilities): boolean => {
  const atomicStatus = chainCapabilities?.atomic?.status;
  return atomicStatus === 'supported' || atomicStatus === 'ready';
};

export const mapContractTransactionRequestToEip5792Call = (
  transactionRequest: WriteContractParameters,
): Eip5792Call => {
  return {
    to: transactionRequest.address,
    abi: transactionRequest.abi,
    functionName: transactionRequest.functionName,
    args: transactionRequest.args,
    value: transactionRequest.value,
  };
};

export const mapTransactionRequestToEip5792Call = (transactionRequest: SendTransactionParameters): Eip5792Call => {
  return {
    to: transactionRequest.to!,
    // TokenPocket on Android bugs out when there is no data key present. At the same time, MetaMask does not allow '0x' as the data value.
    // So we send a zero byte as the data value, this works with both wallets.
    data: transactionRequest.data ?? '0x00',
    value: transactionRequest.value,
  };
};

export const mapWalletCallReceiptToTransactionSubmitted = (
  walletCallReceipt: WalletCallReceipt<bigint, 'success' | 'reverted'>,
  waitForTransactionReceipt: (transactionHash: Hash) => Promise<TransactionReceipt>,
  allowance?: TokenAllowanceData,
  onUpdate?: OnUpdate,
): TransactionSubmitted => {
  const awaitConfirmationAndUpdate = async () => {
    if (walletCallReceipt.status === 'reverted') {
      throw new TransactionRevertedError(walletCallReceipt.transactionHash);
    }

    // The wallet's node can be a block ahead of ours, so the receipt is polled for rather than fetched once
    const receipt = await waitForTransactionReceipt(walletCallReceipt.transactionHash);
    throwIfTransactionReverted(receipt);

    if (allowance && onUpdate) onUpdate(allowance, undefined);
    return receipt;
  };

  return {
    hash: walletCallReceipt.transactionHash,
    confirmation: awaitConfirmationAndUpdate(),
  };
};
